/**
 * Uji satu titik — satu kedalaman dipecahkan, dan pekerjaannya diperlihatkan.
 *
 * Penyelesai multi-mineral memulangkan enam volume per cuplikan dan sebuah
 * kurva inkoherensi, dan tidak satu pun dari keduanya menjawab pertanyaan yang
 * sebenarnya ditanyakan orang di depan layar: dari mana angka ini datang, dan
 * apakah ia pantas dipercaya. Jendela ini menjawabnya pada satu kedalaman —
 * campuran yang ditemukan, apa yang terbaca tiap alat, apa yang diramalkan
 * campuran itu seharusnya terbaca, dan berapa selisih keduanya diukur dengan
 * ketidakpastian alat itu sendiri.
 *
 * Yang tidak dilakukannya: menulis. Bukan run, bukan kurva, bukan baris audit.
 * Karena itu ia terbuka juga bagi peran hanya-baca — justru merekalah yang
 * memeriksa tafsiran orang lain.
 */
import { useEffect, useState } from "react";

import { api } from "../api";
import { DEFAULT_EQUATION, EQUATION_HINT, EQUATION_LABEL } from "../lib/multimin";
import type { MineralModel, PointTest, Well } from "../types";
import { Button, Modal, NumberField, Notice } from "./ui";

/** Angka yang mungkin `null`; sel kosong bukan nol. */
const num = (value: number | null | undefined, digits: number): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : value.toFixed(digits);

/**
 * Berapa desimal pantas bagi sebuah alat.
 *
 * RHOB bergerak di kisaran 1,8–2,9 dan NPHI di 0–0,6, jadi tiga desimal wajar
 * bagi keduanya; GR terentang sampai dua ratus dan DTC sampai dua ratus lebih,
 * dan tiga desimal di sana hanya mencetak derau alat sebagai ketelitian.
 */
const digitsFor = (tool: string): number => (tool === "GR" || tool === "DTC" ? 1 : 3);

/**
 * Seberapa gawat sebuah sisa, dalam satuan ketidakpastian alatnya.
 *
 * Satu sigma adalah selisih yang memang dijanjikan alat itu sendiri, jadi ia
 * tidak diwarnai sama sekali. Di atas tiga, campurannya tidak menjelaskan
 * bacaan itu — dan itulah satu-satunya hal di jendela ini yang pantas berwarna.
 */
const residualTone = (sigma: number | null): string => {
  if (sigma === null || !Number.isFinite(sigma)) return "var(--ink-5)";
  const size = Math.abs(sigma);
  if (size >= 3) return "var(--rose)";
  if (size >= 1.5) return "var(--amber)";
  return "var(--ink-3)";
};

export function PointTestDialog({
  well,
  model,
  top,
  base,
  defaultDepth,
  onClose,
}: {
  well: Well;
  model: MineralModel;
  /** Interval yang sama dengan yang dipakai tombol "Selesaikan interval". */
  top?: number;
  base?: number;
  defaultDepth: number;
  onClose: () => void;
}) {
  const [depth, setDepth] = useState(defaultDepth);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PointTest | null>(null);
  const [error, setError] = useState<string | null>(null);

  const test = async (at: number) => {
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api.post<PointTest>("/api/compute/multimineral/point/", {
          well: well.id,
          mineral_model: model.id,
          top,
          base,
          depth: at,
        }),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  // Dijalankan begitu jendelanya terbuka. Sebuah jendela yang membuka kosong
  // dengan satu kotak angka yang sudah terisi menuntut satu tekanan tombol
  // yang tidak memutuskan apa pun.
  useEffect(() => {
    void test(defaultDepth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cell: React.CSSProperties = {
    padding: "4px 8px",
    textAlign: "right",
    font: "400 10.5px var(--font-mono)",
    borderBottom: "1px solid var(--line-soft)",
    whiteSpace: "nowrap",
  };
  const head: React.CSSProperties = {
    ...cell,
    font: "600 9.5px var(--font-mono)",
    color: "var(--ink-5)",
    borderBottom: "1px solid var(--line-strong)",
  };
  const left: React.CSSProperties = { textAlign: "left" };

  return (
    <Modal
      title="Single-point test"
      width={520}
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <NumberField
            label="Depth"
            unit="m"
            value={depth}
            step={0.5}
            onChange={setDepth}
            disabled={busy}
          />
        </div>
        <Button variant="primary" busy={busy} onClick={() => void test(depth)}>
          Uji
        </Button>
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      {result && (
        <>
          {/* Kedalaman yang diketik hampir tidak pernah jatuh tepat di sebuah
              cuplikan, dan yang dipecahkan adalah cuplikan terdekat. Sebuah
              jendela yang mencetak angka yang diketik di atas jawaban atas
              cuplikan lain mengaku memeriksa tempat yang tidak diperiksanya. */}
          <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
            {`${well.name} · cuplikan ${result.depth.toFixed(2)} m`}
            {Math.abs(result.depth - result.requested_depth) > 0.005
              ? ` (diminta ${result.requested_depth.toFixed(2)} m)`
              : ""}
            {` · ${result.model.name} v${result.model.version}`}
          </span>

          {!result.solved && (
            <Notice>
              {result.missing.length
                ? `Not solved: ${result.missing.join(", ")} is blank at this depth.`
                : "Not solved at this depth."}
            </Notice>
          )}

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...head, ...left }}>Komponen</th>
                <th style={{ ...head, ...left, width: "38%" }} />
                <th style={head}>v/v</th>
                <th style={head}>± σ</th>
              </tr>
            </thead>
            <tbody>
              {result.components.map((component) => (
                <tr key={component.name}>
                  <td style={{ ...cell, ...left }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span
                        style={{
                          width: 9,
                          height: 9,
                          flex: "none",
                          borderRadius: 2,
                          background: component.color,
                        }}
                      />
                      {component.name}
                    </span>
                  </td>
                  <td style={{ ...cell, ...left }}>
                    <span
                      style={{
                        display: "block",
                        height: 7,
                        borderRadius: 2,
                        background: component.color,
                        width: `${Math.max((component.volume ?? 0) * 100, 0)}%`,
                      }}
                    />
                  </td>
                  <td style={cell}>{num(component.volume, 3)}</td>
                  {/* Lebar selangnya, bukan nilainya. "Kuarsa 40 %" dan
                      "kuarsa 40 % ± 25" keluar dari penyelesai yang sama, dan
                      hanya yang kedua boleh dibaca sebagai tebakan. Di atas
                      0,5 data yang ada tidak memisahkan komponen ini sama
                      sekali, dan satu-satunya jalan mempersempitnya adalah
                      menyalakan alat lagi atau menyatakan prior bersigma. */}
                  <td
                    style={{
                      ...cell,
                      color: (component.sigma ?? 0) >= 0.5 ? "var(--amber)" : "var(--ink-5)",
                    }}
                  >
                    {num(component.sigma ?? null, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...head, ...left }}>Alat</th>
                <th style={{ ...head, ...left }}>persamaan</th>
                <th style={head}>measured</th>
                <th style={head}>model</th>
                <th style={head}>residual</th>
                <th style={head}>σ</th>
              </tr>
            </thead>
            <tbody>
              {result.tools.map((tool) => {
                const digits = digitsFor(tool.name);
                return (
                  <tr key={tool.name} style={{ opacity: tool.used ? 1 : 0.62 }}>
                    <td style={{ ...cell, ...left }}>
                      {tool.name}
                      {/* Tiga sebab berbeda sebuah alat tidak terpakai, dan
                          tiga keluhan yang berbeda: kurvanya tidak ada di
                          sumur ini, ia sengaja dimatikan — dan itulah satu
                          -satunya yang berguna sebagai pemeriksaan buta — atau
                          tidak satu pun komponen punya endpoint baginya, yang
                          berarti tidak ada yang bisa diramalkan sama sekali. */}
                      {!tool.used && (
                        <span style={{ color: "var(--ink-6)", fontSize: 9.5 }}>
                          {tool.enabled
                            ? " · curve absent"
                            : tool.declared
                              ? " · buta"
                              : " · no endpoint"}
                        </span>
                      )}
                    </td>
                    {/* Persamaan mana yang meramalkan bacaan ini. "PEF 1,89
                        terukur lawan 1,89 termodelkan" berarti dua hal yang
                        berbeda menurut apakah yang memodelkannya campuran
                        volumetrik atau penampang lintang per satuan volume. */}
                    <td
                      style={{ ...cell, ...left, color: "var(--ink-6)", fontSize: 9.5 }}
                      title={EQUATION_HINT[tool.equation ?? ""] ?? undefined}
                    >
                      {EQUATION_LABEL[tool.equation ?? DEFAULT_EQUATION[tool.name] ?? "linear"]
                        ?? tool.equation}
                    </td>
                    <td style={cell}>{num(tool.measured, digits)}</td>
                    <td style={cell}>{num(tool.reconstructed, digits)}</td>
                    <td style={cell}>{num(tool.residual, digits)}</td>
                    <td style={{ ...cell, color: residualTone(tool.residual_sigma) }}>
                      {num(tool.residual_sigma, 1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <span style={{ font: "400 9.5px/1.6 var(--font-mono)", color: "var(--ink-5)" }}>
            {`${result.solver.nonlinear ? "Levenberg–Marquardt" : "kuadrat-terkecil"} · ` +
              `${result.solver.iterations} iterasi · ` +
              `${result.solver.converged ? "converged" : "did not converge"} · ` +
              // Dalam sigma: 1,0 berarti tiap alat meleset rata-rata satu
              // simpangan bakunya sendiri. Tanpa satuannya angka ini tidak
              // bisa dibandingkan dengan apa pun.
              `incoherence ${num(result.solver.incoherence, 2)} σ · ` +
              `${result.solver.degrees_of_freedom} degrees of freedom` +
              (result.solver.regularised ? ` · regularised (damping ${result.solver.damping})` : "")}
          </span>

          <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
            {"The point is solved from the top of its interval, the way the " +
              "“Solve interval” button solves it — each depth starts from the " +
              "answer of the depth above it, so a point solved on its own returns " +
              "a different number from the one drawn in the volumetric column. A " +
              "row marked “blind” never enters the system: its reconstruction is " +
              "a prediction, and its residual a check no tool that took part can " +
              "give."}
          </span>
        </>
      )}
    </Modal>
  );
}
